import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { getUserCustomSetting, patchUserCustomSetting, UserCustomSetting } from "../api/user";

import styled from "styled-components";
import { Layout, Modal, Button, Input, Space, Tooltip, Popconfirm, message } from "antd";

import { ViewerContext } from "./Viewer";
import { useRecoilState } from "recoil";

import { currentPostAtom, currentSlideAtom, initialSlide } from "../atom";
import { getSlideData, downloadSlide, postAnnotation } from "../api/slideAPI";
import { deletePost, postPost } from "../api/postAPI";
import { DeleteOutlined, LoadingOutlined, LeftOutlined, RightOutlined, EditOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { checkUser, checkUserName } from "../api/authAPI";
import FileSaver from "file-saver";
import axios from "axios";
import { OSDSlideViewer } from "../component/viewer";

const Consultation = () => {
  const [currentPost, setCurrentPost] = useRecoilState(currentPostAtom);
  const [userName, setUserName] = useState<string>("");
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [currentSlide, setCurrentSlide] = useRecoilState(currentSlideAtom);
  const [showDownloadBtn, setShowDownloadBtn] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitleInput, setEditTitleInput] = useState("");
  const [loading, setLoading] = useState(false);

  const [userCustomSet, setUserCustomSet] = useState<UserCustomSetting>({
    useScaleBar: true,
    useMagnificationBar: true,
    useCaptureButton: true,
    useViewerRotationSlider: true,
    useGammaSlider: true,
    useViewerThumbnail: true,
    useAnnotation: true,
    useICCButton: true,
    userGammaValue: 1,
    useInvertedZoom: false,
  });

  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const handleDownload = async () => {
    setIsDownloading(true);
    const fileData = await downloadSlide(currentSlide.slideId);
    if(fileData === false) {
      setIsDownloading(false);
      window.alert("다운로드 권한이 없습니다.");
      return;
    }
    FileSaver.saveAs(fileData, currentPost.slides[0].filename);
    setIsDownloading(false);
  };

  const handleDeletePost = async () => {
    try {
      await deletePost(currentPost.id);
      message.success("게시글이 삭제되었습니다.");
      
      const hasPrevious = location.key !== "default";
      if (hasPrevious) {
        navigate(-1);
      } else {
        navigate("/");
      }
    } catch (error) {
      message.error("삭제 실패");
    }
  };

  const startEditTitle = () => {
    setEditTitleInput(currentPost.title);
    setIsEditingTitle(true);
  };

  const cancelEditTitle = () => {
    setIsEditingTitle(false);
    setEditTitleInput("");
  };

  const saveTitle = async () => {
    if (!editTitleInput.trim()) {
      message.warning("제목을 입력해주세요.");
      return;
    }

    try {
      await postPost(currentPost.id, editTitleInput, currentPost.content);
      
      setCurrentPost((prev) => ({ ...prev, title: editTitleInput }));
      setIsEditingTitle(false);
      message.success("제목이 수정되었습니다.");
    } catch (error) {
      console.error(error);
      message.error("제목 수정에 실패했습니다.");
    }
  };

  const changeSlide = async (newIndex: number) => {
    if (currentPost.slides.length === 0) return;

    const slide = currentPost.slides[newIndex];

    try {
      setLoading(true);
      await axios.post(`${process.env.REACT_APP_DEEPZOOM_URL}/api/hottile?path=${currentPost.id}/${slide.filename}`);
    } catch (error) {
      console.error("Hot tile precaching failed", error);
    }

    setCurrentIndex(newIndex);
    const data = await getSlideData(slide.slideId);
    setCurrentSlide(data);
    setLoading(false);
  };

  const handlePrevSlide = () => {
    if (currentPost.slides.length === 0) return;
    const newIndex = currentIndex === 0 
      ? currentPost.slides.length - 1 
      : currentIndex - 1;
    changeSlide(newIndex);
  };

  const handleNextSlide = () => {
    if (currentPost.slides.length === 0) return;
    const newIndex = currentIndex === currentPost.slides.length - 1 
      ? 0 
      : currentIndex + 1;
    changeSlide(newIndex);
  };

  useEffect(() => {
    checkUserName().then((name) => {
      setUserName(name);
    });

    const getUserSetting = async () => {
      const userSet = await getUserCustomSetting();
      return setUserCustomSet(userSet);
    };

    getUserSetting();

    checkUser().then((role) => {
      setShowDownloadBtn(role === "ADMIN_MAIN" || role === "ADMIN_SUB" || role === "ADVISOR");
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEditingTitle) return; 

      if (currentPost.slides.length === 0) return;

      if (e.key === "ArrowLeft") {
        handlePrevSlide();
      } else if (e.key === "ArrowRight") {
        handleNextSlide();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentIndex, currentPost.slides, isEditingTitle]);

  useEffect(() => {
    if (currentPost.slides[0]) {
      const initSlide = async () => {
        try {
          setLoading(true);
          const slide = currentPost.slides[0];
          await axios.post(`${process.env.REACT_APP_DEEPZOOM_URL}/api/hottile?path=${currentPost.id}/${slide.filename}`);

          const data = await getSlideData(slide.slideId);
          setCurrentSlide(data);
        } catch (error) {
          console.error(error);
          if (currentPost.slides[0]) {
            const data = await getSlideData(currentPost.slides[0].slideId);
            setCurrentSlide(data);
          }
        } finally {
          setLoading(false);
        }
      };

      initSlide();
    } else {
      setCurrentSlide(initialSlide);
    }
  }, [currentPost]);

  useEffect(() => {
    const updateUserSet = async () => {
      await patchUserCustomSetting(userCustomSet);
    };

    window.addEventListener("beforeunload", updateUserSet);

    return () => {
      window.removeEventListener("beforeunload", updateUserSet);
    };
  }, [userCustomSet]);

  useEffect(() => {
    const handleOutPath = async () => {
      await patchUserCustomSetting(userCustomSet);
    };

    return () => {
      handleOutPath();
    };
  }, [location, location.pathname, userCustomSet]);
  

  const isWriter = userName === currentPost.writer;

  return (
    <ViewerContext.Provider value={{ userCustomSet, setUserCustomSet }}>
      <Layout.Content style={{ minHeight: "80vh" }}>
        <StHeaderContainer>
          <div className="title-area">
            {isEditingTitle ? (
              <Input 
                value={editTitleInput}
                onChange={(e) => setEditTitleInput(e.target.value)}
                onPressEnter={saveTitle}
                maxLength={100}
                style={{ fontSize: '1.5rem', fontWeight: 'bold', height: 'auto' }}
              />
            ) : (
              <h1>{currentPost.title}</h1>
            )}
          </div>

          <div className="action-area">
            {isWriter && (
              <Space>
                {isEditingTitle ? (
                  <>
                    <Tooltip title="저장">
                      <Button type="primary" shape="circle" icon={<CheckOutlined />} onClick={saveTitle} />
                    </Tooltip>
                    <Tooltip title="취소">
                      <Button shape="circle" icon={<CloseOutlined />} onClick={cancelEditTitle} />
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <Tooltip title="제목 수정">
                      <Button type="text" shape="circle" icon={<EditOutlined />} onClick={startEditTitle} />
                    </Tooltip>
                    
                    <Popconfirm
                      title="게시글 삭제"
                      description="정말로 이 게시글을 삭제하시겠습니까?"
                      onConfirm={handleDeletePost}
                      okText="삭제"
                      cancelText="취소"
                      okButtonProps={{ danger: true }}
                      placement="bottomRight"
                      getPopupContainer={() => document.body}
                    >
                      <Tooltip title="게시글 삭제">
                        <Button type="text" danger shape="circle" icon={<DeleteOutlined />} />
                      </Tooltip>
                    </Popconfirm>
                  </>
                )}
              </Space>
            )}
          </div>
        </StHeaderContainer>
        {currentPost.slides[0] &&
          <>
            <div style={{ position: "relative", width: "80%", margin: "0 auto", height: "70vh" }}>
              <OSDSlideViewer
                postId={currentPost.id}
                filename={currentSlide?.filename}
                slideId={currentSlide?.slideId}
                isAnno={true}
                isInfo={true}
                isReset={false}
                isUserSet={true}
                isSaveSet={false}
                loading={loading}
                onGetAnnotations={async (slideId) => {
                  const data = await getSlideData(slideId);
                  return data;
                }}
                onSaveAnnotation={async (slideId, path, name) => {
                  await postAnnotation(slideId, path, name);
                }}
              />
            </div>
            <div style={{ width: "80%", margin: "0 auto", marginTop: "2rem", position: "relative" }}>
              <Button
                shape="circle"
                icon={<LeftOutlined />}
                onClick={handlePrevSlide}
                style={{
                  position: "absolute",
                  left: -50,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 10,
                  width: 45,
                  height: 45,
                  backgroundColor: "rgba(30, 46, 58, 0.85)",
                  color: "#fff",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  transition: "all 0.3s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(30, 46, 58, 1)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.25)";
                  e.currentTarget.style.transform = "translateY(-50%) scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(30, 46, 58, 0.85)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.15)";
                  e.currentTarget.style.transform = "translateY(-50%) scale(1)";
                }}
              />
              
              <Button
                shape="circle"
                icon={<RightOutlined />}
                onClick={handleNextSlide}
                style={{
                  position: "absolute",
                  right: -50,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 10,
                  width: 45,
                  height: 45,
                  backgroundColor: "rgba(30, 46, 58, 0.85)",
                  color: "#fff",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  transition: "all 0.3s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(30, 46, 58, 1)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.25)";
                  e.currentTarget.style.transform = "translateY(-50%) scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(30, 46, 58, 0.85)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.15)";
                  e.currentTarget.style.transform = "translateY(-50%) scale(1)";
                }}
              />

              {currentPost.slides.map((slide, index) => (
                <img
                  key={`${slide.pathFileName}_${index}`}
                  alt={`slide_${slide.pathFileName}`}
                  src={
                    process.env.REACT_APP_BACKEND_URL +
                    "/api/photo/" +
                    slide.thumbnail
                  }
                  onClick={() => changeSlide(index)}
                  style={{
                    cursor: "pointer",
                    width: "100px",
                    height: "100px",
                    margin: "5px",
                    border: index === currentIndex
                        ? "3px solid #1677ff"
                        : "1px solid #d9d9d9",
                    borderRadius: 8,
                    transition: "all 0.2s",
                  }}
                  />
              ))}
            </div>
            <div style={{ width: "80%", margin: "1rem auto", textAlign: "right" }}>
              {showDownloadBtn && <Button onClick={handleDownload}>File Download</Button>}
            </div>
            {isDownloading && 
              <Modal open={isDownloading} centered closable={false} footer={null}>
                Downloading...  <LoadingOutlined />
              </Modal>
            }
          </>}
      </Layout.Content>
    </ViewerContext.Provider>
  );
};

const StHeaderContainer = styled.div`
  width: 80%;
  margin: 1rem auto;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .title-area {
    flex: 1;
    margin-right: 1rem;
    
    h1 {
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .action-area {
    flex-shrink: 0;
  }
`;


export default Consultation;